/* eslint-disable react/require-default-props -- defaults via destructuring; no defaultProps (React 19) */
import React, { useState, useMemo, useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import Typography from '../Typography';
import Button from '../Button';
import { FlexRow, FlexColumn } from '../GridsAndLayouts/GridsAndLayouts';
import { ChevronDownIcon, CloseIcon, CheckIcon } from '../Icons';

const MultiselectGroup = ({
  DropdownComponent,
  label,
  groups,
  value,
  defaultValue,
  onChange,
  menuTitle = 'Filters',
  menuIcon,
  clearAllLabel,
  showCheckboxes = true,
  maxVisibleChips = 2,
  placeholder = 'Select…',
  disabled = false,
  error = false,
  align = 'left',
  className = '',
  id,
}) => {
  const generatedId = useId();
  const controlId = id || generatedId;
  const listboxId = `${controlId}-listbox`;

  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue || []);
  const selectedValues = isControlled ? value : internalValue;
  const [menuOpen, setMenuOpen] = useState(false);

  const setSelectedValues = useCallback(
    (next) => {
      if (!isControlled) {
        setInternalValue(next);
      }

      if (onChange) {
        onChange(next);
      }
    },
    [isControlled, onChange]
  );

  const flatOptions = useMemo(
    () =>
      groups.flatMap((group) =>
        (group.options || []).map((option) => ({
          ...option,
          groupLabel: group.label,
        }))
      ),
    [groups]
  );

  const optionMap = useMemo(() => {
    const map = new Map();
    flatOptions.forEach((option) => {
      map.set(String(option.value), option);
    });
    return map;
  }, [flatOptions]);

  const isSelected = useCallback(
    (optionValue) => selectedValues.some((selectedValue) => String(selectedValue) === String(optionValue)),
    [selectedValues]
  );

  const toggleValue = useCallback(
    (optionValue) => {
      if (disabled) return;

      const normalizedValue = String(optionValue);
      const nextValues = isSelected(optionValue)
        ? selectedValues.filter((selectedValue) => String(selectedValue) !== normalizedValue)
        : [...selectedValues, optionValue];

      setSelectedValues(nextValues);
    },
    [disabled, isSelected, selectedValues, setSelectedValues]
  );

  const removeValue = useCallback(
    (event, optionValue) => {
      event.stopPropagation();

      if (disabled) return;

      setSelectedValues(selectedValues.filter((selectedValue) => String(selectedValue) !== String(optionValue)));
    },
    [disabled, selectedValues, setSelectedValues]
  );

  const handleClearAll = useCallback(
    (event) => {
      event.stopPropagation();
      if (disabled) return;
      setSelectedValues([]);
    },
    [disabled, setSelectedValues]
  );

  const handleDropdownOpen = useCallback(() => {
    setMenuOpen(true);
  }, []);

  const handleDropdownClose = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const selectedChips = selectedValues.map((selectedValue) => optionMap.get(String(selectedValue))).filter(Boolean);
  const visibleChips = selectedChips.slice(0, maxVisibleChips);
  const overflowCount = Math.max(0, selectedChips.length - maxVisibleChips);

  const renderOption = (option) => {
    const selected = isSelected(option.value);

    return (
      <FlexRow
        key={String(option.value)}
        role="option"
        aria-selected={selected}
        className={[
          'ds-dropdown-multiselect__option',
          'ds-dropdown-multiselect-group__option',
          showCheckboxes && 'ds-dropdown-multiselect__option--checkbox',
          selected && 'ds-dropdown-multiselect__option--selected',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => toggleValue(option.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleValue(option.value);
          }
        }}
        tabIndex={0}
        align="start"
      >
        {showCheckboxes && (
          <FlexRow
            className="ds-dropdown-multiselect__checkbox-wrap"
            align="center"
            justify="center"
            inline
            fullWidth={false}
          >
            <FlexRow
              className={[
                'ds-dropdown-multiselect__checkbox-indicator',
                selected && 'ds-dropdown-multiselect__checkbox-indicator--checked',
              ]
                .filter(Boolean)
                .join(' ')}
              align="center"
              justify="center"
              inline
              fullWidth={false}
              aria-hidden
            >
              {selected && <CheckIcon size={12} strokeWidth={2.4} className="ds-icon--color-white" />}
            </FlexRow>
          </FlexRow>
        )}
        <Typography
          component="span"
          variant="body1"
          fontWeight="medium"
          className="ds-dropdown-multiselect__option-label"
        >
          {option.label}
        </Typography>
      </FlexRow>
    );
  };

  const trigger = (
    <FlexRow
      className={`ds-dropdown-multiselect__field ${disabled ? 'ds-dropdown-multiselect__field--disabled' : ''} ${
        menuOpen ? 'ds-dropdown-multiselect__field--open' : ''
      } ${error ? 'ds-dropdown-multiselect__field--error' : ''}`.trim()}
      id={controlId}
      align="center"
    >
      {label && (
        <Typography
          component="span"
          variant="body2"
          color="muted"
          className="ds-dropdown-multiselect__label"
          id={`${controlId}-label`}
        >
          {label}
        </Typography>
      )}
      {menuIcon && (
        <FlexRow
          className="ds-dropdown-multiselect__menu-icon"
          align="center"
          justify="center"
          inline
          fullWidth={false}
          aria-hidden
        >
          {menuIcon}
        </FlexRow>
      )}
      <FlexRow className="ds-dropdown-multiselect__value" align="center" fullWidth={false}>
        {selectedChips.length === 0 && (
          <Typography component="span" variant="body1" className="ds-dropdown-multiselect__placeholder">
            {placeholder}
          </Typography>
        )}
        {selectedChips.length > 0 && (
          <FlexRow className="ds-dropdown-multiselect__chips" align="center" fullWidth>
            {visibleChips.map((option) => (
              <FlexRow
                key={String(option.value)}
                className="ds-dropdown-multiselect__chip"
                align="center"
                inline
                fullWidth={false}
              >
                <Typography
                  component="span"
                  variant="body2"
                  fontWeight="medium"
                  className="ds-dropdown-multiselect__chip-text"
                >
                  {option.label}
                </Typography>
                <Button
                  variant="tertiary"
                  size="sm"
                  type="button"
                  className="ds-dropdown-multiselect__chip-remove"
                  icon={<CloseIcon size={12} strokeWidth={1.2} />}
                  iconPosition="right"
                  disabled={disabled}
                  aria-label={`Remove ${option.label}`}
                  onClick={(event) => removeValue(event, option.value)}
                />
              </FlexRow>
            ))}
            {overflowCount > 0 && (
              <Typography
                component="span"
                variant="body2"
                fontWeight="medium"
                className="ds-dropdown-multiselect__overflow"
              >
                +{overflowCount}
              </Typography>
            )}
          </FlexRow>
        )}
      </FlexRow>
      <FlexRow className="ds-dropdown-multiselect__actions" align="center" inline fullWidth={false} gap={2}>
        <FlexRow
          className={`ds-dropdown-multiselect__chevron ${menuOpen ? 'ds-dropdown-multiselect__chevron--open' : ''}`}
          align="center"
          justify="center"
          inline
          fullWidth={false}
          aria-hidden
        >
          <ChevronDownIcon />
        </FlexRow>
      </FlexRow>
    </FlexRow>
  );

  const rootClass = ['ds-dropdown--multiselect', 'ds-dropdown--multiselect-group', className].filter(Boolean).join(' ');

  return (
    <DropdownComponent
      className={rootClass.trim()}
      trigger={trigger}
      align={align}
      disabled={disabled}
      onOpen={handleDropdownOpen}
      onClose={handleDropdownClose}
    >
      <FlexColumn className="ds-dropdown-multiselect-group__panel" align="stretch" fullWidth>
        {(menuTitle || clearAllLabel) && (
          <FlexRow
            className="ds-dropdown-multiselect-group__header"
            align="center"
            justify={clearAllLabel ? 'between' : 'start'}
            fullWidth
            onMouseDown={(event) => event.stopPropagation()}
          >
            {menuTitle && (
              <FlexRow
                className="ds-dropdown-multiselect-group__title-row"
                align="center"
                inline
                fullWidth={false}
                gap={8}
              >
                {/* {menuIcon && (
                  <FlexRow
                    className="ds-dropdown-multiselect-group__menu-icon"
                    align="center"
                    justify="center"
                    inline
                    fullWidth={false}
                    aria-hidden
                  >
                    {menuIcon}
                  </FlexRow>
                )} */}
                <Typography
                  component="span"
                  variant="bodyExtraSmall"
                  fontWeight="medium"
                  className="ds-dropdown-multiselect-group__title"
                >
                  {menuTitle}
                </Typography>
              </FlexRow>
            )}
            {clearAllLabel && (
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                className="ds-dropdown-multiselect-group__clear"
                disabled={disabled || selectedValues.length === 0}
                onClick={handleClearAll}
              >
                {clearAllLabel}
              </Button>
            )}
          </FlexRow>
        )}

        <FlexColumn
          id={listboxId}
          className="ds-dropdown-multiselect-group__list"
          role="listbox"
          aria-multiselectable
          aria-labelledby={label ? `${controlId}-label` : undefined}
          align="start"
          fullWidth
        >
          {groups.map((group, groupIndex) => (
            <FlexColumn
              key={group.id || group.label || groupIndex}
              className="ds-dropdown-multiselect-group__section"
              align="start"
              fullWidth
            >
              {group.label && (
                <Typography
                  component="span"
                  variant="body2"
                  color="muted"
                  className="ds-dropdown-multiselect-group__section-label"
                >
                  {group.label}
                </Typography>
              )}
              <FlexColumn className="ds-dropdown-multiselect-group__section-options" align="start" fullWidth>
                {(group.options || []).map((option) => renderOption(option))}
              </FlexColumn>
            </FlexColumn>
          ))}
        </FlexColumn>
      </FlexColumn>
    </DropdownComponent>
  );
};

MultiselectGroup.propTypes = {
  DropdownComponent: PropTypes.elementType,
  label: PropTypes.string,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          label: PropTypes.node.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  defaultValue: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onChange: PropTypes.func,
  menuTitle: PropTypes.string,
  menuIcon: PropTypes.node,
  clearAllLabel: PropTypes.string,
  showCheckboxes: PropTypes.bool,
  maxVisibleChips: PropTypes.number,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  align: PropTypes.oneOf(['left', 'right', 'center']),
  className: PropTypes.string,
  id: PropTypes.string,
};

export default MultiselectGroup;
